import React, { StrictMode } from 'react';
import { connect, Provider } from 'react-redux';
import { EnhancedStore } from '@reduxjs/toolkit';
import { HelmetContext, Provider as HelmetProvider } from '@okapi/helmet-react';
import { RenderCallback } from '@okapi/core';
import { Provider as RemoteProvider, Remote } from '@okapi/remote';
import { GlobalRouter, Router } from '@okapi/router-react';
import { YMInitializer } from 'react-yandex-metrika';
import { selectIsMobileView } from 'selectors';
import { Meta, ErrorBoundary, GoogleAnalyticsScript } from 'common/components';
import { App } from '../../app';
import { RootState } from '../../types/root-state';
import { getGTMCode, getYaCode } from '../../selectors';
import { servicesLandingAppStyles } from './styles';

export type EntryProps = {
	router: GlobalRouter;
	remoteContext: RenderCallback['remoteContext'];
} & (ClientSideEntryProps | ServerSideEntryProps);

type ClientSideEntryProps = {
	store: EnhancedStore;
	helmetContext?: never;
};

type ServerSideEntryProps = {
	store: EnhancedStore;
	helmetContext?: HelmetContext;
};

export function createEntry({ router, helmetContext, store, remoteContext }: EntryProps) {
	const ConnectedGoogleAnalyticsScript = connect((state: RootState) => ({
		id: getGTMCode(state),
	}))(GoogleAnalyticsScript);

	const yaCode = Number(getYaCode(store.getState()));
	const isMobileView = selectIsMobileView(store.getState());

	return () => (
		<StrictMode>
			<ErrorBoundary>
				<HelmetProvider context={helmetContext}>
					<Provider store={store}>
						<RemoteProvider context={remoteContext}>
							<Router router={router}>
								<Meta />
								<div
									className={`services-landings-app ${servicesLandingAppStyles}`}
								>
									{!isMobileView && (
										<Remote
											widget="header-footer/header"
											id="services-landings-header"
											fallback={<div>Error</div>}
										/>
									)}
									<main className="services-landings-app__main">
										<App />
									</main>
									{!isMobileView && (
										<Remote
											widget="header-footer/footer"
											id="services-landings-footer"
											fallback={<div>Error</div>}
										/>
									)}
								</div>
								<ConnectedGoogleAnalyticsScript />
								{yaCode ? (
									<YMInitializer
										accounts={[yaCode]}
										options={{
											clickmap: true,
											trackLinks: true,
											accurateTrackBounce: true,
											webvisor: true,
										}}
										version="2"
									/>
								) : null}
							</Router>
						</RemoteProvider>
					</Provider>
				</HelmetProvider>
			</ErrorBoundary>
		</StrictMode>
	);
}
